// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Modal } from "react-bootstrap";
import { Form, Col, Dropdown as BSDrowdown, Row } from "react-bootstrap";
import InputNumber from "rc-input-number";

import {
  ActionType,
  IAction,
  IActionChangeRfid,
  IActionTypeConfirmPopup,
  raiseAction,
} from "actions";
import { DataState } from "types";
import { IState } from "states/states";
import { IModalRfid, ModalPanel } from "states/guistates";
import { IOcppCharger, IRfid } from "states/ocppstates";
import { getLocalDate, getLocalDateTimeIso, getLocalTime, isSet } from "utils";
import { parseDateTimeEvent } from "guiutils";
import Dropdown from "view/comp/Dropdown";

import SaveFeedback from "view/comp/SaveFeedback";
import TariffAssociations from "view/comp/TariffAssociations";

enum eRfidLimitPeriod {
  ONETIME = "---",
  DAY = "DAY",
  MONTH = "MONTH",
  YEAR = "YEAR",
}

type IModalRfidItemProps = IModalRfid & {
  isOpen: boolean;
  dataState: DataState;
  rfids: IRfid[];
  rfid: IRfid | undefined;
  allChargers: IOcppCharger[];
  canModifyRfidLinks: boolean;
  isRestrictedAccess: boolean; // allow full access in original site only
};

const mapStateToProps = (state: IState) => {
  const rfid = state.ocpp.rfid.find((curItem) => curItem.id === state.gui.modalRfid?.id);
  const props: IModalRfidItemProps = {
    ...state.gui.modalRfid!,
    isOpen: state.gui.modal.panel === ModalPanel.RFID,
    dataState: state.gui.modal.dataState,
    rfids: state.ocpp.rfid,
    rfid,
    allChargers: state.ocpp.charger,
    canModifyRfidLinks: isSet(state.site.siteList) ? state.site.siteList?.length > 0 : false,
    isRestrictedAccess: isSet(rfid) && rfid.siteId !== state.cached.selectedSite,
  };
  return props;
};

interface IModalRfidState {
  wasOpen: boolean;
  isChanged: boolean;
  isPinGeneration: boolean;
  rfid: string;
  name: string | undefined;
  chargerRestricted: boolean;
  chargers: number[];
  limitStart: number | undefined;
  limitEnd: number | undefined;
  limitEnergy: number | undefined;
  limitCost: number | undefined;
  limitPeriod: eRfidLimitPeriod | undefined;
  isLoginAllowed: boolean;
  isChargerPowerCtrlAllowed: boolean;
  maxPower: number | undefined;
  category: string | undefined;
  additionalData: string | undefined;
  carName: string | undefined;
  carBattery: number | undefined;
}

interface IModalRfidItemDispatch {
  onCheckRfid: (rfid: string) => void;
  onConfirmDelete: (id: number, title: string, message: string) => void;
  onSaveClick: (id: number | undefined, state: IModalRfidState) => void;
  onCancelClick: () => void;
  onLinkEdit: (id: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IModalRfidItemDispatch = {
    onCheckRfid: (rfid: string) => {
      dispatch(raiseAction(ActionType.ACT_CHECK_OCPP_RFIDS_REQ, rfid));
    },
    onConfirmDelete: (id: number, title: string, message: string) => {
      const confirmPayload: IActionChangeRfid = {
        id,
        rfid: undefined,
      };
      const payload: IActionTypeConfirmPopup = {
        title,
        message,
        action: ActionType.ACT_SAVE_OCPP_RFID_REQ,
        actionPayload: confirmPayload,
      };
      dispatch(raiseAction(ActionType.ACT_CONFIRM_POPUP, payload));
    },
    onSaveClick: (id: number | undefined, state: IModalRfidState) => {
      // remove unused/renamed properties from state
      const { wasOpen, isChanged, isPinGeneration, ...data } = state;
      const payload: IActionChangeRfid = {
        id,
        ...data,
      };
      dispatch(raiseAction(ActionType.ACT_SAVE_OCPP_RFID_REQ, payload));
    },
    onCancelClick: () => {
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
    },
    onLinkEdit: (id: number) => {
      dispatch(raiseAction(ActionType.ACT_LINK_RFID, id));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  selectHint: {
    id: "press CTRL (CMD on Mac) key to select mutliple items",
  },
  limitEnergyHint: {
    id: "Energy limit in kWh. You may also set a start date, which is also the start of energy metering for this limit or a period for which this limit applies.",
  },
  limitEnergyResetHint: {
    id: "This resets the counter for the used energy by setting the start date to the current timestamp.",
  },
  limitCostHint: {
    id: "Cost limit. You may also set a start date, which is also the start of this cost limit or a period for which this limit applies.",
  },
  limitPowerHint: {
    id: "Power limit in kW. You can restrict the maximum charging speed using this value.",
  },
  rfidChecking: {
    id: "Checking for valid charger card number...",
  },
  rfidInvalid: {
    id: "This charger card number is not valid. It either does not fulfill requirements on card number or is already in use.",
  },
  duplicateRfid: {
    id: "Card number already in use.",
  },
  generatePin: {
    id: "Generate a valid Pin code for charging stations.",
  },
  confirmDeleteTitle: {
    id: "Deleting charging card",
  },
  confirmDeleteBody: {
    id: "This will delete this charging card. The charging data itself will still be available. Are you sure?",
  },
});

class ModalRfidSettingsComponent extends React.Component<
  IModalRfidItemProps & IModalRfidItemDispatch & WrappedComponentProps,
  IModalRfidState
> {
  state: IModalRfidState = {
    wasOpen: false,
    isChanged: false,
    isPinGeneration: false,
    rfid: "",
    name: "",
    chargerRestricted: false,
    chargers: [],
    limitStart: undefined,
    limitEnd: undefined,
    limitEnergy: undefined,
    limitCost: undefined,
    limitPeriod: undefined,
    isLoginAllowed: false,
    isChargerPowerCtrlAllowed: false,
    maxPower: undefined,
    category: undefined,
    additionalData: undefined,
    carName: undefined,
    carBattery: undefined,
  };

  componentDidUpdate() {
    // check for changes in old value
    if (this.props.isOpen !== this.state.wasOpen) {
      this.setState({
        wasOpen: this.props.isOpen,
      });
      if (this.props.isOpen) {
        this.setState({
          isChanged: false,
          isPinGeneration: false,
          rfid: this.props.rfid?.rfid ?? this.props.addedrfid ?? "",
          name: this.props.rfid?.name ?? "",
          chargerRestricted: this.props.rfid?.chargerRestricted ?? false,
          chargers: this.props.rfid?.chargers ?? [],
          limitStart: this.props.rfid?.limitStart,
          limitEnd: this.props.rfid?.limitEnd,
          limitEnergy: this.props.rfid?.limitEnergy,
          limitCost: this.props.rfid?.limitCost,
          limitPeriod: this.props.rfid?.limitPeriod,
          isLoginAllowed: this.props.rfid?.accessRights.isLoginAllowed ?? false,
          isChargerPowerCtrlAllowed:
            this.props.rfid?.accessRights.isChargerPowerCtrlAllowed ?? false,
          maxPower: this.props.rfid?.maxPower,
          category: this.props.rfid?.category,
          additionalData: this.props.rfid?.additionalData,
          carName: this.props.rfid?.carName,
          carBattery: this.props.rfid?.carBattery,
        });
      }
    }

    // check for result of pin code generations
    else if (this.state.isPinGeneration && this.props.checkResult) {
      // store generated Pin
      this.setState({
        isPinGeneration: false,
        rfid: this.props.checkResult,
      });
    }
  }

  private isRfidInputDataValid() {
    return this.state.rfid && !this.state.rfid.includes("_") && this.state.rfid.length > 3;
  }

  public render() {
    if (!this.props.isOpen) return <></>; // avoid rendering of inexisting data

    return (
      <Modal show={this.props.isOpen} onHide={this.props.onCancelClick}>
        <Modal.Header closeButton>
          <FormattedMessage id="Charging Card Settings" />
        </Modal.Header>
        <Modal.Body>{this.renderSettings()}</Modal.Body>
        <Modal.Footer>
          <div className="button-bar-left">
            {isSet(this.props.id) &&
              (this.props.isRestrictedAccess ? (
                <div className="mt-2">
                  <FormattedMessage id="Foreign Card" />
                </div>
              ) : (
                <Button
                  variant="danger"
                  disabled={this.props.dataState === DataState.SAVE_REQ}
                  onClick={(_evt) =>
                    this.props.onConfirmDelete(
                      this.props.id!,
                      this.props.intl.formatMessage(messages.confirmDeleteTitle),
                      this.props.intl.formatMessage(messages.confirmDeleteBody),
                    )
                  }
                >
                  <FormattedMessage id="Delete Card" />
                </Button>
              ))}
          </div>
          {isSet(this.props.id) &&
            this.props.canModifyRfidLinks &&
            !this.props.isRestrictedAccess && (
              <div className="button-bar-left">
                <Button
                  variant="secondary"
                  disabled={this.props.dataState === DataState.SAVE_REQ || this.state.isChanged}
                  onClick={(_evt) => this.props.onLinkEdit(this.props.id!)}
                >
                  <FormattedMessage id="Sites" /> ({this.props.rfid?.siteIds?.length})
                </Button>
              </div>
            )}
          <div className="button-bar-right">
            <SaveFeedback
              dataState={this.props.dataState}
              className="me-2"
              lastError={this.props.saveError}
            />
            <Button
              id="cancelRfidSettings"
              variant="secondary"
              className="me-2"
              onClick={this.props.onCancelClick}
            >
              <FormattedMessage id="Cancel" />
            </Button>
            <Button
              id="saveRfidSettings"
              variant="primary"
              onClick={this.props.onSaveClick.bind(this, this.props.id, this.state)}
              disabled={
                this.props.dataState === DataState.SAVE_REQ ||
                !this.state.isChanged ||
                !this.isRfidInputDataValid() ||
                !this.state.name
              }
            >
              <FormattedMessage id="Save" />
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }

  private renderSettings() {
    return (
      <>
        {this.renderRfid()}
        <Form.Group as={Row} className="mb-3">
          <Form.Label htmlFor="name" column sm={3}>
            <FormattedMessage id="Name" />
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              id="name"
              type="text"
              value={this.state.name ?? ""}
              className={this.state.name ? undefined : "input-error"}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  name: event?.currentTarget.value,
                });
              }}
            />
          </Col>
        </Form.Group>
        {this.renderCategorySetting()}
        <Form.Group as={Row} className="mb-3">
          <Form.Label htmlFor="additionalData" column sm={3}>
            <FormattedMessage id="Additional Data" />
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              id="additionalData"
              type="text"
              value={this.state.additionalData ?? ""}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  additionalData: event?.currentTarget.value,
                });
              }}
            />
          </Col>
        </Form.Group>
        {this.renderRestriction()}
        {this.renderAccessRights()}
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>
            <FormattedMessage id="Cost Tariffs" />
          </Form.Label>
          <Col sm={9} className="mt-1">
            <TariffAssociations rfidId={this.props.rfid?.id} />
          </Col>
        </Form.Group>
        {this.renderCar()}
      </>
    );
  }

  private renderRfid() {
    const isRfidCheckOngoing = this.isRfidInputDataValid() && this.props.checkResult === undefined;
    const isRfidValid =
      this.state.rfid === this.props.rfid?.rfid ||
      (this.isRfidInputDataValid() && this.props.checkResult === this.state.rfid);

    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label htmlFor="rfid" column sm={3}>
          <FormattedMessage id="Card number" />
        </Form.Label>
        {this.props.isRestrictedAccess ? (
          <Col sm={9} style={{ paddingTop: 6 }}>
            {this.props.rfid?.rfid}
          </Col>
        ) : (
          <>
            <Col sm={6}>
              <Form.Control
                id="rfid"
                type="text"
                value={this.state.rfid}
                className={
                  isRfidCheckOngoing ? "input-checking" : isRfidValid ? undefined : "input-error"
                }
                title={
                  isRfidCheckOngoing
                    ? this.props.intl.formatMessage(messages.rfidChecking)
                    : isRfidValid
                    ? undefined
                    : this.props.intl.formatMessage(messages.rfidInvalid)
                }
                onChange={(event) => {
                  const rfid = event?.currentTarget.value.toLowerCase().replace(/\s+/g, "");
                  this.props.onCheckRfid(rfid);
                  this.setState({
                    isChanged: true,
                    rfid,
                  });
                }}
              />
            </Col>
            <Col sm={3} style={{ padding: 0 }}>
              <Button
                id="generatePin"
                variant="secondary"
                title={this.props.intl.formatMessage(messages.generatePin)}
                onClick={(_event) => {
                  this.props.onCheckRfid("#000000");
                  this.setState({
                    isPinGeneration: true,
                  });
                }}
              >
                <FormattedMessage id="Generate Pin" />
              </Button>
            </Col>
          </>
        )}
      </Form.Group>
    );
  }

  private renderRestriction() {
    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label column sm={3}>
          <FormattedMessage id="Card restrictions" />
        </Form.Label>
        <Col sm={9}>
          <Form.Check
            id="chargerRestricted"
            className="mb-2 mt-2"
            checked={this.state.chargerRestricted}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                chargerRestricted: event.currentTarget.checked,
              });
            }}
            label={this.renderLimitChargerLabel()}
          />
          <Form.Check
            id="limitStart"
            className="mb-2"
            checked={this.state.limitStart ? true : false}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                limitStart: event.currentTarget.checked ? Math.round(Date.now() / 1000) : undefined,
              });
            }}
            label={this.renderLimitStartLabel()}
          />
          <Form.Check
            id="limitEnd"
            className="mb-2"
            checked={this.state.limitEnd ? true : false}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                limitEnd: event.currentTarget.checked ? Math.round(Date.now() / 1000) : undefined,
              });
            }}
            label={this.renderLimitEndLabel()}
          />
          <Form.Check
            id="limitEnergy"
            className="mb-2"
            checked={isSet(this.state.limitEnergy)}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                limitEnergy: event.currentTarget.checked ? 100 : undefined,
              });
            }}
            label={this.renderLimitEnergyLabel()}
          />
          <Form.Check
            id="limitCost"
            className="mb-2"
            checked={isSet(this.state.limitCost)}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                limitCost: event.currentTarget.checked ? 100 : undefined,
              });
            }}
            label={this.renderLimitCostLabel()}
          />
          <Form.Check
            id="limitPower"
            className="mb-2"
            checked={isSet(this.state.maxPower)}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                maxPower: event.currentTarget.checked ? 100 : undefined,
              });
            }}
            label={this.renderLimitPowerLabel()}
          />
        </Col>
      </Form.Group>
    );
  }

  private renderLimitChargerLabel() {
    const chargerItems = this.props.allChargers
      .filter((curItem) => isSet(curItem.fk_charger))
      .map((curItem) => {
        return (
          <option key={`${curItem.fk_charger}`} value={`${curItem.fk_charger}`}>
            {curItem.name ?? curItem.chargepoint}
          </option>
        );
      });

    return this.state.chargerRestricted ? (
      <>
        <FormattedMessage id="Limit the card to these chargers" />:
        {this.state.chargerRestricted && (
          <>
            <Form.Select
              multiple={true}
              title={this.props.intl.formatMessage(messages.selectHint)}
              value={this.state.chargers.map((curItem) => `${curItem}`)}
              onChange={(event) => {
                const list = Array.from(event.currentTarget.selectedOptions).map((item) =>
                  parseInt(item.value),
                );
                this.setState({
                  isChanged: true,
                  chargers: list,
                });
              }}
            >
              {chargerItems}
            </Form.Select>
          </>
        )}
      </>
    ) : (
      <FormattedMessage id="Limit the card to chargers" />
    );
  }

  private renderLimitStartLabel() {
    return (
      <>
        <FormattedMessage id="Limit the card from" />
        {isSet(this.state.limitStart) && this.state.limitStart !== 0 && <>:</>}
        {isSet(this.state.limitStart) &&
          this.state.limitStart !== 0 &&
          (Modernizr.inputtypes["datetime-local"] ? (
            <input
              type="datetime-local"
              name="timestamp"
              className="me-1"
              defaultValue={getLocalDateTimeIso(this.state.limitStart * 1000)}
              onChange={(event) => {
                const newTimestampMs = parseDateTimeEvent(event, this.state.limitStart);
                // gets undefined using "clear", but also by entering a date with zero
                if (newTimestampMs) {
                  this.setState({
                    isChanged: true,
                    limitStart: Math.round(newTimestampMs / 1000),
                  });
                }
              }}
            />
          ) : (
            <>
              <br />
              <input
                type="date"
                name="date"
                value={getLocalDate(this.state.limitStart * 1000)}
                onChange={(event) => {
                  const newTimestampMs = parseDateTimeEvent(event, this.state.limitStart);
                  this.setState({
                    isChanged: true,
                    limitStart: newTimestampMs ? Math.round(newTimestampMs / 1000) : undefined,
                  });
                }}
              />{" "}
              <input
                type="time"
                name="time"
                value={getLocalTime(this.state.limitStart)}
                onChange={(event) => {
                  const newTimestampMs = parseDateTimeEvent(event, this.state.limitStart);
                  this.setState({
                    isChanged: true,
                    limitStart: newTimestampMs ? Math.round(newTimestampMs / 1000) : undefined,
                  });
                }}
              />
            </>
          ))}
      </>
    );
  }

  private renderLimitEndLabel() {
    return (
      <>
        <FormattedMessage id="Limit the card until" />
        {isSet(this.state.limitEnd) && this.state.limitEnd !== 0 && <>:</>}
        {isSet(this.state.limitEnd) &&
          this.state.limitEnd !== 0 &&
          (Modernizr.inputtypes["datetime-local"] ? (
            <input
              type="datetime-local"
              name="timestamp"
              className="me-1"
              defaultValue={getLocalDateTimeIso(this.state.limitEnd * 1000)}
              onChange={(event) => {
                const newTimestampMs = parseDateTimeEvent(event, this.state.limitEnd);
                // gets undefined using "clear", but also by entering a date with zero
                if (newTimestampMs) {
                  this.setState({
                    isChanged: true,
                    limitEnd: Math.round(newTimestampMs / 1000),
                  });
                }
              }}
            />
          ) : (
            <>
              <br />
              <input
                type="date"
                name="date"
                style={{ width: 130 }}
                value={getLocalDate(this.state.limitEnd * 1000)}
                onChange={(event) => {
                  const newTimestampMs = parseDateTimeEvent(event, this.state.limitEnd);
                  this.setState({
                    isChanged: true,
                    limitEnd: newTimestampMs ? Math.round(newTimestampMs / 1000) : undefined,
                  });
                }}
              />{" "}
              <input
                type="time"
                name="time"
                style={{ width: 145 }}
                value={getLocalTime(this.state.limitEnd)}
                onChange={(event) => {
                  const newTimestampMs = parseDateTimeEvent(event, this.state.limitEnd);
                  this.setState({
                    isChanged: true,
                    limitEnd: newTimestampMs ? Math.round(newTimestampMs / 1000) : undefined,
                  });
                }}
              />
            </>
          ))}
      </>
    );
  }

  private renderLimitEnergyLabel() {
    return isSet(this.state.limitEnergy) ? (
      <>
        <FormattedMessage id="Limit the total energy to" />
        <span className="me-1">:</span>
        {isSet(this.state.limitEnergy) && (
          <>
            <br />
            <span className="nowrap me-3">
              <InputNumber
                id="limitEnergyValue"
                type="number"
                title={this.props.intl.formatMessage(messages.limitEnergyHint)}
                min={1}
                value={this.state.limitEnergy}
                onChange={(number) => {
                  if (isSet(number)) {
                    this.setState({
                      isChanged: true,
                      limitEnergy: number,
                    });
                  }
                }}
              />{" "}
              kWh
            </span>{" "}
            <span className="nowrap">
              <FormattedMessage id="every" />
              &nbsp;
              <Dropdown
                items={this.renderLimitPeriodItems()}
                curItem={this.props.intl.formatMessage({
                  id: `Rfid_LimitEnergyPeriod_${
                    isSet(this.state.limitPeriod)
                      ? this.state.limitPeriod
                      : eRfidLimitPeriod.ONETIME
                  }`,
                })}
              />
            </span>
            {
              /* disabled for now */ false && (
                <div className="mt-2">
                  <Button
                    id="resetEnergy"
                    variant="secondary"
                    onClick={(_event) => {
                      this.setState({
                        isChanged: true,
                        limitStart: Math.round(Date.now() / 1000),
                      });
                    }}
                    title={this.props.intl.formatMessage(messages.limitEnergyResetHint)}
                  >
                    <FormattedMessage id="Reset Counter" />
                  </Button>
                </div>
              )
            }
          </>
        )}
      </>
    ) : (
      <FormattedMessage id="Limit the total energy" />
    );
  }

  private renderLimitCostLabel() {
    return isSet(this.state.limitCost) ? (
      <>
        <FormattedMessage id="Limit the total cost to" />
        <span className="me-1">:</span>
        {isSet(this.state.limitCost) && (
          <>
            <br />
            <span className="nowrap me-3">
              <InputNumber
                id="limitCostValue"
                type="number"
                title={this.props.intl.formatMessage(messages.limitCostHint)}
                min={1}
                value={this.state.limitCost}
                onChange={(number) => {
                  if (isSet(number)) {
                    this.setState({
                      isChanged: true,
                      limitCost: number,
                    });
                  }
                }}
              />{" "}
              EUR
            </span>{" "}
            <span className="nowrap">
              <FormattedMessage id="every" />
              &nbsp;
              <Dropdown
                items={this.renderLimitPeriodItems()}
                curItem={this.props.intl.formatMessage({
                  id: `Rfid_LimitEnergyPeriod_${
                    isSet(this.state.limitPeriod)
                      ? this.state.limitPeriod
                      : eRfidLimitPeriod.ONETIME
                  }`,
                })}
              />
            </span>
          </>
        )}
      </>
    ) : (
      <FormattedMessage id="Limit the total cost" />
    );
  }

  private renderLimitPowerLabel() {
    return isSet(this.state.maxPower) ? (
      <>
        <FormattedMessage id="Limit the charging power to" />
        <span className="me-1">:</span>
        {isSet(this.state.maxPower) && (
          <>
            <br />
            <span className="nowrap">
              <InputNumber
                id="maxPower"
                type="number"
                title={this.props.intl.formatMessage(messages.limitPowerHint)}
                min={1}
                max={1000}
                value={this.state.maxPower}
                onChange={(value) => {
                  this.setState({
                    isChanged: true,
                    maxPower: value ?? undefined, // null -> undefined
                  });
                }}
              />{" "}
              kW
            </span>
          </>
        )}
      </>
    ) : (
      <FormattedMessage id="Limit the charging power" />
    );
  }

  private renderAccessRights() {
    return (
      <>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={3}>
            <FormattedMessage id="Card access" />
          </Form.Label>
          <Col sm={9} style={{ paddingTop: 6 }}>
            <Form.Check
              id="loginAllowed"
              className="mb-2"
              checked={this.state.isLoginAllowed}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  isLoginAllowed: event.currentTarget.checked,
                });
              }}
              label={<FormattedMessage id="Allow login with card" />}
            />
            <Form.Check
              id="isChargerPowerCtrlAllowed"
              className="mb-2"
              checked={this.state.isChargerPowerCtrlAllowed}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  isChargerPowerCtrlAllowed: event.currentTarget.checked,
                });
              }}
              label={<FormattedMessage id="Charger power control" />}
            />
          </Col>
        </Form.Group>
      </>
    );
  }

  private renderCar() {
    return (
      <Form.Group as={Row} className="mb-3">
        <Form.Label column sm={3} style={{ marginBottom: 0 }}>
          <FormattedMessage id="Vehicle" />
        </Form.Label>
        <Col sm={9} style={{ paddingTop: 6 }}>
          <Form.Check
            id="car"
            className="mb-2"
            checked={isSet(this.state.carName) || isSet(this.state.carBattery)}
            onChange={(event) => {
              this.setState({
                isChanged: true,
                carName: event.currentTarget.checked ? "" : undefined,
                carBattery: event.currentTarget.checked ? 100 : undefined,
              });
            }}
            label={
              isSet(this.state.carName) || isSet(this.state.carBattery) ? (
                <>
                  <FormattedMessage id="Vehicle information" />
                  <span className="me-1">:</span>
                </>
              ) : (
                <FormattedMessage id="Add vehicle information" />
              )
            }
          />
          {(isSet(this.state.carName) || isSet(this.state.carBattery)) && (
            <table>
              <tbody>
                <tr>
                  <td style={{ paddingLeft: 0 }}>
                    <Form.Label htmlFor="name" sm={4}>
                      <FormattedMessage id="Identifier" />:
                    </Form.Label>
                  </td>
                  <td>
                    <Form.Control
                      id="carName"
                      name="carName"
                      value={this.state.carName}
                      onChange={(event) => {
                        this.setState({
                          isChanged: true,
                          carName: event?.currentTarget.value,
                        });
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: 0 }}>
                    <Form.Label htmlFor="name" sm={4} style={{ paddingTop: 0 }}>
                      <FormattedMessage id="Battery" />:
                    </Form.Label>
                  </td>
                  <td className="nowrap">
                    <InputNumber
                      id="carBattery"
                      type="number"
                      min={1}
                      max={1000}
                      value={this.state.carBattery}
                      onChange={(value) => {
                        this.setState({
                          isChanged: true,
                          carBattery: value ?? undefined, // null -> undefined
                        });
                      }}
                    />{" "}
                    kWh
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </Col>
      </Form.Group>
    );
  }

  private renderCategorySetting() {
    const availableCategories = Array.from(
      new Set(this.props.rfids.map((rfid) => rfid.category)),
    ).map((category) => (
      <option key={`cat_${category}`} id={`cat_${category}`} value={category}></option>
    ));

    return (
      <>
        <Form.Group as={Row} className="mb-3">
          <Form.Label htmlFor="name" column sm={3}>
            <FormattedMessage id="Category" />
          </Form.Label>
          <Col sm={9}>
            <Form.Control
              id="category"
              list="categoryData"
              name="category"
              autoComplete="true"
              value={this.state.category}
              onChange={(event) => {
                this.setState({
                  isChanged: true,
                  category: event?.currentTarget.value,
                });
              }}
            />
            <datalist id="categoryData">{availableCategories}</datalist>
          </Col>
        </Form.Group>
      </>
    );
  }

  private renderLimitPeriodItems() {
    return Object.values(eRfidLimitPeriod).map((period) => {
      return (
        <BSDrowdown.Item
          key={`lep_${period}`}
          onClick={() =>
            this.setState({
              isChanged: true,
              limitPeriod: period === eRfidLimitPeriod.ONETIME ? undefined : period,
            })
          }
        >
          <span>{this.props.intl.formatMessage({ id: `Rfid_LimitEnergyPeriod_${period}` })}</span>
        </BSDrowdown.Item>
      );
    });
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ModalRfidSettingsComponent));
